import { operateMaxShowLength } from '@/store/dict/config'
import { couponActivityPageList, userGetCouponPageList, verifyCouponPageList } from '@/api/domain/activity'

const sourceTypeMap = {
  1: '手动发放',
  2: '自动发放',
  3: '主动领取',
  4: '抽奖获得',
  5: '权益活动奖励',
  19: '租车支付奖励',
}

const stateMap = {
  1: '待使用',
  2: '已使用',
  3: '已失效',
}

const verifyOperateTypeMap = {
  1: '使用',
  2: '取消退回',
  3: '退款退回',
}

function formatTime(value) {
  if (value === null || value === undefined || value === '') return '--'
  const text = String(value)
  if (/^\d+$/.test(text)) {
    const d = new Date(Number(text))
    const pad = (num) => String(num).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  }
  return text
}

function toMs(dateTimeText = '') {
  if (!dateTimeText) return ''
  return new Date(dateTimeText).getTime()
}

function formatPeriod(startTime, endTime) {
  const start = formatTime(startTime)
  const end = formatTime(endTime)
  if (start === '--' || end === '--') return '--'
  return `${start} ~ ${end}`
}

function canJumpDriverList(customerName) {
  return customerName !== null && customerName !== undefined && customerName !== '' && customerName !== '--'
}

function canJumpOrderList(orderId) {
  return orderId !== null && orderId !== undefined && orderId !== '' && orderId !== '--'
}

function canJumpCouponActivity(couponTemplateId) {
  return couponTemplateId !== null && couponTemplateId !== undefined && couponTemplateId !== '' && couponTemplateId !== '--'
}

async function queryRecordList(params = {}, activeTab = 'grant') {
  if (activeTab === 'grant') {
    const response = await userGetCouponPageList(params)
    const { code, data = {} } = response || {}
    if (code !== 1) return response

    const items = (data.items || []).map((item) => {
      const driverId = item.customerId ?? '--'
      return {
        userId: item.userId ?? '--',
        driverId,
        customerName: item.customerName || '',
        couponCode: item.couponId ?? '--',
        couponTemplateId: item.couponTemplateId ?? '--',
        couponTemplateName: item.couponTemplateName || '--',
        source: sourceTypeMap[item.sourceType] || '--',
        amountText:
          item.discountAmountDesc ?? '--',
        periodText: formatPeriod(item.startTime, item.endTime),
        actionTime: formatTime(item.gmtCreate),
        statusText: stateMap[item.state] || '--',
      }
    })

    return {
      ...response,
      data: {
        ...data,
        items,
        totalNum: data.totalNum || 0,
      },
    }
  }

  const response = await verifyCouponPageList(params)
  const { code, data = {} } = response || {}
  if (code !== 1) return response

  const items = (data.items || []).map((item) => ({
    userId: item.userId ?? '--',
    driverId: item.customerId ?? '--',
    customerName: item.customerName || '',
    couponCode: item.couponId ?? '--',
    couponTemplateId: item.couponTemplateId ?? '--',
    couponTemplateName: item.couponTemplateName || '--',
    source: item.feeTypeName || '--',
    amountText:
      item.realDiscountAmountStr
      || item.discountAmountDesc
      || item.receivableAmount
      || '--',
    receivableAmount: item.receivableAmount ?? '--',
    realDiscountAmountStr: item.realDiscountAmountStr ?? '--',
    periodText: item.orderId || '--',
    middleSubOrderId: item.middleSubOrderId || '',
    actionTime: formatTime(item.operateTime),
    statusText: item.operateTypeName || verifyOperateTypeMap[item.operateType] || '--',
  }))

  return {
    ...response,
    data: {
      ...data,
      items,
      totalNum: data.totalNum || 0,
    },
  }
}

export const configDataFn = ({
  activeTab = 'grant',
  couponTemplateId = '',
  showGrantUserIdFilter = false,
  hideVerifySourceFilter = false,
  hideCouponTemplateFields = false,
  hideVerifyCouponTemplateFields = false,
  initialFormData = {},
  onShowDeductionDetail = null,
  onJumpDriverList = null,
  onJumpCouponActivity = null,
  onJumpOrderList = null,
  createElement = null,
}) => {
  const isGrant = activeTab === 'grant'
  const showSourceFilter = isGrant || !hideVerifySourceFilter
  const showCouponTemplateFields = !hideCouponTemplateFields && (isGrant || !hideVerifyCouponTemplateFields)
  const couponTemplateNameOptions = {
    label: 'couponTemplateName',
    value: 'couponTemplateName',
    list: [],
  }
  let couponTemplateNameList = []
  let hasLoadedCouponTemplateNameList = false
  let couponTemplateNameLoading = false

  const updateCouponTemplateNameOptions = (query = '') => {
    const keyword = String(query).trim()
    const list = !keyword
      ? couponTemplateNameList
      : couponTemplateNameList.filter((item) => String(item.couponTemplateName || '').includes(keyword))
    couponTemplateNameOptions.list = list
  }

  const getCouponTemplateIdByName = (couponTemplateName) => {
    if (!couponTemplateName) return ''
    const matched = couponTemplateNameList.find((item) => item.couponTemplateName === couponTemplateName)
    return matched?.couponTemplateId || ''
  }

  const loadCouponTemplateNameList = async () => {
    if (hasLoadedCouponTemplateNameList || couponTemplateNameLoading) return
    couponTemplateNameLoading = true
    try {
      const response = await couponActivityPageList({
        pageNum: 1,
        pageSize: 200,
      })
      const { code, data = {} } = response || {}
      if (code !== 1) return
      const rawItems = Array.isArray(data.items) ? data.items : []
      const nameSet = new Set()
      couponTemplateNameList = rawItems.reduce((result, item) => {
        const couponTemplateName = item.name
        const tempCouponTemplateId = item.couponTemplateId || item.id || ''
        if (!couponTemplateName || nameSet.has(couponTemplateName)) return result
        nameSet.add(couponTemplateName)
        result.push({ couponTemplateName, couponTemplateId: tempCouponTemplateId })
        return result
      }, [])
      hasLoadedCouponTemplateNameList = true
    } finally {
      couponTemplateNameLoading = false
    }
  }

  return {
    formData: {
      formProps: {
        ref: 'searchForm',
        model: {
          ...initialFormData,
        },
      },
      list: [
        {
          el: 'input',
          itemConfig: {
            label: isGrant ? '券码' : '核销券码',
            prop: 'couponId',
          },
          elConfig: {
            clearable: true,
            placeholder: '请输入',
          },
          directives: [{ name: 'number', arg: 'integer', value: -1 }],
        },
        ...isGrant && showGrantUserIdFilter ? [
          {
            el: 'input',
            itemConfig: {
              label: '用户ID',
              prop: 'userId',
            },
            elConfig: {
              clearable: true,
              placeholder: '请输入',
            },
            directives: [{ name: 'number', arg: 'integer', value: -1 }],
          },
        ] : [],
        ...showCouponTemplateFields
          ? [
            {
              el: 'input',
              itemConfig: {
                label: '优惠券ID',
                prop: 'couponTemplateId',
              },
              elConfig: {
                clearable: true,
                placeholder: '请输入',
              },
              directives: [{ name: 'number', arg: 'integer', value: -1 }],
            },
            {
              el: 'select',
              itemConfig: {
                label: '优惠券名称',
                prop: 'couponTemplateName',
              },
              elConfig: {
                clearable: true,
                placeholder: '请输入',
                remote: true,
                filterable: true,
                'reserve-keyword': true,
                remoteMethod: async (query) => {
                  await loadCouponTemplateNameList()
                  updateCouponTemplateNameOptions(query)
                },
                options: couponTemplateNameOptions,
                events: {
                  focus: async () => {
                    await loadCouponTemplateNameList()
                    updateCouponTemplateNameOptions('')
                  },
                  clear: () => {
                    updateCouponTemplateNameOptions('')
                  },
                },
              },
            },
          ]
          : [],
        ...!isGrant ? [
          {
            el: 'input',
            itemConfig: {
              label: '订单编号',
              prop: 'orderId',
            },
            elConfig: {
              clearable: true,
              placeholder: '请输入',
            },
          },
        ] : [],
        {
          el: 'input',
          itemConfig: {
            label: '司机ID',
            prop: 'customerId',
          },
          elConfig: {
            clearable: true,
            placeholder: '请输入',
          },
          directives: [{ name: 'number', arg: 'integer', value: -1 }],
        },
        ...showSourceFilter
          ? [
            {
              el: 'select',
              itemConfig: {
                label: isGrant ? '发放来源' : '核销来源',
                prop: isGrant ? 'sourceType' : 'feeTypeName',
              },
              elConfig: {
                options: {
                  label: 'label',
                  value: 'value',
                  list: isGrant
                    ? [
                      { label: '全部', value: '' },
                      { label: '手动发放', value: 1 },
                      { label: '自动发放', value: 2 },
                      { label: '主动领取', value: 3 },
                      // { label: '抽奖获得', value: 4 },
                      // { label: '权益活动奖励', value: 5 },
                      { label: '租车支付奖励', value: 19 },
                    ]
                    : [
                      { label: '全部', value: '' },
                      { label: '订单核销', value: '订单核销' },
                    ],
                },
              },
            },
          ]
          : [],
        {
          el: 'select',
          itemConfig: {
            label: isGrant ? '使用状态' : '核销状态',
            prop: isGrant ? 'state' : 'operateType',
          },
          elConfig: {
            options: {
              label: 'label',
              value: 'value',
              list: isGrant
                ? [
                  { label: '全部', value: '' },
                  { label: '待使用', value: 1 },
                  { label: '已使用', value: 2 },
                  { label: '已失效', value: 3 },
                ]
                : [
                  { label: '全部', value: '' },
                  { label: '已核销', value: 1 },
                  { label: '取消退回', value: 2 },
                  // { label: '退款退回', value: 3 },
                ],
            },
          },
        },
        {
          el: 'date-picker',
          itemConfig: {
            label: isGrant ? '领取时间' : '核销时间',
            prop: 'timeRange',
          },
          elConfig: {
            type: 'daterange',
            format: 'yyyy-MM-dd',
            'value-format': 'yyyy-MM-dd HH:mm:ss',
            'default-time': ['00:00:00', '23:59:59'],
            'range-separator': '至',
            'start-placeholder': '开始时间',
            'end-placeholder': '结束时间',
          },
        },
      ],
      dealWithSearch: (formData) => {
        const [timeStart = '', timeEnd = ''] = formData.timeRange || []
        const couponTemplateIdFromName = getCouponTemplateIdByName(formData.couponTemplateName)
        const params = isGrant
          ? {
            ...formData,
            couponTemplateId:
              formData.couponTemplateId || couponTemplateIdFromName || couponTemplateId || '',
            takeStartTime: toMs(timeStart),
            takeEndTime: toMs(timeEnd),
          }
          : {
            ...formData,
            couponTemplateId: formData.couponTemplateId || couponTemplateIdFromName || couponTemplateId || '',
            operateStartTime: toMs(timeStart),
            operateEndTime: toMs(timeEnd),
          }
        delete params.timeRange
        return params
      },
    },
    isShowDepartment: false,
    tableData: {
      columns: [
        {
          name: 'userId',
          columnProps: {
            label: '用户ID',
            prop: 'userId',
            width: 170,
          },
        },
        {
          name: 'driverId',
          columnProps: {
            label: '司机ID',
            prop: 'driverId',
            width: 170,
          },
          render: ({ row }) => {
            const { driverId } = row
            const { customerName } = row
            if (!canJumpDriverList(customerName) || typeof onJumpDriverList !== 'function' || typeof createElement !== 'function') {
              return driverId
            }
            return createElement('span', {
              class: 'a-hover-link',
              on: {
                click: () => onJumpDriverList(customerName),
              },
            }, driverId)
          },
        },
        {
          name: 'couponCode',
          columnProps: {
            label: '券码',
            prop: 'couponCode',
            width: 130,
          },
        },
        ...showCouponTemplateFields
          ? [
            {
              name: 'couponTemplateId',
              columnProps: {
                label: '优惠券ID',
                prop: 'couponTemplateId',
                width: 130,
              },
              render: ({ row }) => {
                const { couponTemplateId: tempCouponTemplateId } = row
                if (
                  !canJumpCouponActivity(tempCouponTemplateId)
                  || typeof onJumpCouponActivity !== 'function'
                  || typeof createElement !== 'function'
                ) {
                  return tempCouponTemplateId
                }
                return createElement('span', {
                  class: 'a-hover-link',
                  on: {
                    click: () => onJumpCouponActivity(tempCouponTemplateId),
                  },
                }, tempCouponTemplateId)
              },
            },
            {
              name: 'couponTemplateName',
              columnProps: {
                label: '优惠券名称',
                prop: 'couponTemplateName',
                width: 160,
              },
            },
          ]
          : [],
        ...isGrant ? [
          {
            name: 'source',
            columnProps: {
              label: '发放来源',
              prop: 'source',
              width: 120,
            },
          },
          {
            name: 'amountText',
            columnProps: {
              label: '领取面值',
              prop: 'amountText',
              width: 110,
            },
          },
        ] : [
          {
            name: 'receivableAmount',
            columnProps: {
              label: '应收金额(元)',
              prop: 'receivableAmount',
              width: 130,
            },
          },
          {
            name: 'realDiscountAmountStr',
            columnProps: {
              label: '实际抵扣金额(元)',
              prop: 'realDiscountAmountStr',
              width: 140,
            },
          },
        ],
        {
          name: 'periodText',
          columnProps: {
            label: isGrant ? '生效周期' : '核销订单ID',
            prop: 'periodText',
            width: isGrant ? 330 : 180,
          },
          render: ({ row }) => {
            const { periodText } = row
            if (
              isGrant
              || !canJumpOrderList(periodText)
              || typeof onJumpOrderList !== 'function'
              || typeof createElement !== 'function'
            ) {
              return periodText
            }
            return createElement('span', {
              class: 'a-hover-link',
              on: {
                click: () => onJumpOrderList(periodText),
              },
            }, periodText)
          },
        },
        {
          name: 'actionTime',
          columnProps: {
            label: isGrant ? '领取时间' : '核销时间',
            prop: 'actionTime',
            width: 170,
          },
        },
        {
          name: 'statusText',
          columnProps: {
            label: isGrant ? '使用状态' : '核销状态',
            prop: 'statusText',
            minWidth: 110,
          },
        },
        ...!isGrant ? [
          {
            name: 'operate',
            columnProps: {
              label: '操作',
              prop: 'operate',
              fixed: 'right',
              minWidth: 100,
            },
            maxShowLength: operateMaxShowLength,
            type: 'operate',
            getOperateList: ({ row }) => ([
              {
                name: 'deductionDetail',
                label: '抵扣明细',
                action: () => onShowDeductionDetail && onShowDeductionDetail(row),
              },
            ]),
          },
        ] : [],
      ],
      dealTableData: (items = []) => items,
      apiFn: (params) => queryRecordList(params, activeTab),
    },
  }
}
